import { ChevronDown } from "lucide-react";
import React, { useState } from "react";
import SearchBar from "./ui/SearchBar/SearchBar";
import { ChipInput } from "./ui/Input/chipInput";
import CustomCheckbox from "./ui/Checkbox/CustomCheckbox";
import CustomSwitch from "./ui/Switch/CustomSwitch";
import { showWarning } from "@/shared/utils/toast";
import RangeSlider from "./ui/RangeSlider/RangeSlider";

export function filterFieldShowsAppliedState(
  fieldKey,
  filters,
  appliedFilters,
  defaultSnapshot,
) {
  const cur = filters?.[fieldKey];
  const def = defaultSnapshot != null ? defaultSnapshot[fieldKey] : undefined;
  if (defaultSnapshot != null) {
    const same = JSON.stringify(cur ?? null) === JSON.stringify(def ?? null);
    if (!same) return true;
  }
  const raw = appliedFilters?.[fieldKey];
  if (raw == null || raw === false || raw === "") return false;
  if (Array.isArray(raw)) return raw.length > 0;
  if (typeof raw === "boolean") return raw === true;
  if (typeof raw === "object") {
    if (Array.isArray(raw.values)) return raw.values.length > 0;
    if ("value" in raw) {
      const v = raw.value;
      if (typeof v === "boolean") return v;
      if (Array.isArray(v)) return v.length > 0;
      return v != null && String(v).trim() !== "";
    }
    return Object.keys(raw).length > 0;
  }
  return !!raw;
}

function countAppliedFilterValues(value) {
  if (value == null || value === false || value === "") return 0;
  if (Array.isArray(value)) return value.filter(Boolean).length;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "object") {
    if (Array.isArray(value.values)) {
      return value.values.filter(Boolean).length;
    }
    if ("value" in value) return countAppliedFilterValues(value.value);
    return Object.keys(value).length;
  }
  return 1;
}

function getAppliedFilterCount(fieldKey, filters, appliedFilters) {
  const filterCount = countAppliedFilterValues(filters?.[fieldKey]);
  if (filterCount > 0) return filterCount;
  return countAppliedFilterValues(appliedFilters?.[fieldKey]);
}

const Accordion = ({
  filterFields,
  filters,
  setFilters,
  appliedFilters,
  defaultFiltersSnapshot,
}) => {
  const [openFields, setOpenFields] = useState(null);

  const toggleField = (key) => {
    setOpenFields((prev) => (prev === key ? null : key));
  };

  return (
    <div className="px-3 overflow-auto">
      {filterFields?.map((field) => {
        const isOpen = openFields === field.key;
        const isActive =
          filterFieldShowsAppliedState(
            field.key,
            filters,
            appliedFilters,
            defaultFiltersSnapshot,
          ) || false;
        const appliedCount = getAppliedFilterCount(
          field.key,
          filters,
          appliedFilters,
        );
        return (
          <div key={field.id} className="flex flex-col gap-3">
            <div
              className={`py-5 flex justify-between items-center cursor-pointer ${
                isOpen
                  ? "border-none"
                  : isActive
                    ? "border-b border-secondary/25"
                    : "border-b border-gray-200 dark:border-gray-800"
              }`}
              onClick={() => toggleField(field.key)}
            >
              <div className="flex gap-1.5 items-center min-w-0">
                {field.icon}
                <div className="flex items-center gap-1.5 min-w-0">
                  <p
                    className={`text-xs truncate ${
                      isActive
                        ? "font-semibold text-gray-900 dark:text-gray-100"
                        : "font-medium text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {field.title}
                  </p>
                  {isActive ? (
                    <span
                      className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-secondary"
                      title="Applied to current search"
                    />
                  ) : null}
                  {isActive && appliedCount > 0 ? (
                    <span className="shrink-0 rounded-full bg-secondary-transparent px-2 py-0.5 text-[0.65rem] font-semibold text-secondary">
                      {appliedCount} applied
                    </span>
                  ) : null}
                </div>
              </div>
              <div
                className={`transition-all duration-200 shrink-0 ${
                  isOpen ? "rotate-180" : ""
                }`}
              >
                <ChevronDown
                  size={20}
                  strokeWidth={2}
                  className={isActive ? "text-secondary" : "text-gray-500 dark:text-gray-400"}
                />
              </div>
            </div>

            <div
              className={`transition-[max-height] duration-500 ease-in-out ${
                isOpen
                  ? "max-h-96 overflow-y-auto overscroll-contain pb-4"
                  : "max-h-0 overflow-hidden"
              }`}
            >
              {isOpen && (
                <div className="p-0.5">
                  {field.type === "search" ? (
                    <SearchBar
                      value={filters[field.key] || ""}
                      setFilterValue={(val) =>
                        setFilters((prev) => ({ ...prev, [field.key]: val }))
                      }
                      onChange={(val) =>
                        setFilters((prev) => ({ ...prev, [field.key]: val }))
                      }
                      placeholder={field.placeholder}
                      className="w-full"
                    />
                  ) : field.type === "toggle" ? (
                    <div className="flex items-center justify-between gap-3">
                      {/* field.disabledReason: the toggle is locked and the reason says why. */}
                      <span className={`text-xs ${field.disabledReason ? "text-gray-500 dark:text-gray-400" : "text-gray-700 dark:text-gray-300"}`}>
                        {field.disabledReason || (filters[field.key] ? "Enabled" : "Disabled")}
                      </span>
                      <div
                        // A disabled button swallows clicks, so let them reach this wrapper to explain why.
                        className={field.disabledReason ? "shrink-0 cursor-not-allowed opacity-50 [&>button]:pointer-events-none" : "shrink-0"}
                        onClick={field.disabledReason ? () => showWarning(field.disabledReason) : undefined}
                      >
                        <CustomSwitch
                          size="sm"
                          disabled={Boolean(field.disabledReason)}
                          isChecked={!field.disabledReason && !!filters[field.key]}
                          onChange={(val) =>
                            setFilters((prev) => ({
                              ...prev,
                              [field.key]: val,
                            }))
                          }
                        />
                      </div>
                    </div>
                  ) : field.type === "range" ? (
                    <RangeSlider
                      options={field.options || []}
                      selected={filters[field.key] || []}
                      onChange={(val) =>
                        setFilters((prev) => ({
                          ...prev,
                          [field.key]: val,
                        }))
                      }
                    />
                  ) : field.type === "checkbox" ? (
                    <div
                      className="flex items-center gap-2 cursor-pointer"
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          [field.key]: !prev[field.key],
                        }))
                      }
                    >
                      <div className="pointer-events-none">
                        <CustomCheckbox
                          checked={!!filters[field.key]}
                          onChange={() => {}}
                        />
                      </div>
                      <span className="text-xs text-gray-700 dark:text-gray-300">
                        {filters[field.key] ? "Enabled" : "Disabled"}
                      </span>
                    </div>
                  ) : field.type === "checkboxMulti" || field.type === "checkboxSingle" ? (
                    <div className="flex flex-col gap-2.5 pt-1">
                      {(field.options || []).map((opt) => {
                        const sel = filters[field.key] || [];
                        const checked = Array.isArray(sel)
                          ? sel.includes(opt.value)
                          : sel === opt.value;
                        const toggle = () =>
                          setFilters((prev) => {
                            if (field.type === "checkboxSingle") {
                              const cur = Array.isArray(prev[field.key])
                                ? prev[field.key][0]
                                : prev[field.key];
                              if (cur === opt.value) {
                                return { ...prev, [field.key]: [] };
                              }
                              return { ...prev, [field.key]: [opt.value] };
                            } else {
                              const cur = [...(prev[field.key] || [])];
                              const i = cur.indexOf(opt.value);
                              if (i >= 0) cur.splice(i, 1);
                              else cur.push(opt.value);
                              return { ...prev, [field.key]: cur };
                            }
                          });
                        return (
                          <div
                            key={opt.value}
                            className="flex cursor-pointer items-center gap-2 rounded-md px-1 py-0.5"
                            role="presentation"
                            onClick={toggle}
                          >
                            <div className="pointer-events-none">
                              <CustomCheckbox
                                checked={checked}
                                onChange={() => {}}
                              />
                            </div>
                            <span className="text-xs text-gray-700 dark:text-gray-300">
                              {opt.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ChipInput
                      defaultChips={filters[field.key] || []}
                      placeholder={field.placeholder}
                      dropdownOptions={field.options || []}
                      onChange={(chips) =>
                        setFilters((prev) => ({
                          ...prev,
                          [field.key]: chips,
                        }))
                      }
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Accordion;
